import { BasePageDto } from "@/types/dto/base-page-dto";
import { GradeDto } from "@/types/dto/grade-dto";
import { AxiosInstance } from "axios";
import { BaseResponse } from "../types/dto/base-response";
import { CreateGradePayload } from "../types/payload/create-grade-payload";
import { GetAdminPagePayload } from "../types/payload/get-admin-page-payload";
import { UpdateGradePayload } from "../types/payload/update-grade-payload";
import { baseApi } from "./base-api";

// GradeService API 엔드포인트 정의
enum GradeServiceEndpoint {
  CreateGrade = "/grade", // 등급 생성
  DeleteGrade = "/grade", // 등급 삭제
  UpdateGrade = "/grade", // 등급 수정
  GetGradeById = "/grade", // ID로 등급 조회
  GetGradePage = "/grades", // 페이지별 등급 조회
}

class GradeService {
  private static instance: GradeService; // 싱글톤 인스턴스
  private _api: AxiosInstance; // Axios 인스턴스

  // private 생성자로 외부에서 직접 인스턴스 생성 불가능
  private constructor(api: AxiosInstance) {
    this._api = api;
  }

  // 싱글톤 패턴을 사용하여 인스턴스를 반환
  static getInstance(apiClient = baseApi): GradeService {
    if (!GradeService.instance) {
      GradeService.instance = new GradeService(apiClient);
    }
    return GradeService.instance;
  }

  // 등급 생성 메서드
  async create(
    payload: CreateGradePayload
  ): Promise<BaseResponse<GradeDto, CreateGradePayload>> {
    try {
      const response = await this._api.post<
        BaseResponse<GradeDto, CreateGradePayload>
      >(`${GradeServiceEndpoint.CreateGrade}`, {
        name: payload.name,
        requirement: Number(payload.requirement),
        discountRate: Number(payload.discountRate),
      });
      return response.data;
    } catch (error) {
      console.error("Error create:", error);
      throw error;
    }
  }

  // ID로 등급 수정 메서드
  async updateById(
    id: number,
    payload: UpdateGradePayload
  ): Promise<BaseResponse<GradeDto, UpdateGradePayload>> {
    try {
      const response = await this._api.patch(
        `${GradeServiceEndpoint.UpdateGrade}/${id}`,
        {
          name: payload.name,
          requirement: Number(payload.requirement),
          discountRate: Number(payload.discountRate),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updateById:", error);
      throw error;
    }
  }

  // ID로 등급 조회 메서드
  async getById(id: number): Promise<BaseResponse<GradeDto, void>> {
    try {
      const response = await this._api.get<BaseResponse<GradeDto, void>>(
        `${GradeServiceEndpoint.GetGradeById}/${id}`
      );
      return response.data;
    } catch (error) {
      console.error("Error getById:", error);
      throw error;
    }
  }

  // ID로 등급 삭제 메서드
  async deleteById(id: number): Promise<BaseResponse<void, void>> {
    try {
      const response = await this._api.delete<BaseResponse<void, void>>(
        `${GradeServiceEndpoint.DeleteGrade}/${id}`
      );
      return response.data;
    } catch (error) {
      console.error("Error deleteById:", error);
      throw error;
    }
  }

  // 페이지별 등급 조회 메서드
  async getPage(
    payload: GetAdminPagePayload
  ): Promise<BaseResponse<BasePageDto<GradeDto>, GetAdminPagePayload>> {
    try {
      const response = await this._api.get(GradeServiceEndpoint.GetGradePage, {
        params: {
          page: payload.page,
          size: payload.size,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error getPage:", error);
      throw error;
    }
  }
}

// GradeService 인스턴스 내보내기
export const gradeService = GradeService.getInstance(baseApi);
